import { asIfAccessToken } from '@/features/as-if/asIfView';
import {
  deploymentCredentialScope,
  effectiveDeploymentAddress,
  isBuiltInDeploymentAddress,
} from '@/features/onboarding/deploymentAddress';
import type { LocalDataDriver, LocalDataRecord } from './types';

export const LOCAL_SYNC_DB_NAME = 'featit-local-sync';
const DB_NAME = LOCAL_SYNC_DB_NAME;
const DB_VERSION = 1;
const STORE_NAME = 'records';
/** I-1127: how long opening the store may take before a read gives up on the cache. */
export const INDEXED_DB_OPEN_DEADLINE_MS = 3_000;

/**
 * FR-9 (I-1120): the local copy belongs to the deployment it was read from, so its name is keyed by
 * that deployment the way the credential slots are. Pointing the app at another deployment then
 * starts from an empty copy instead of showing the first deployment's rows under the second's name.
 * The built-in deployment keeps the unscoped name it has always used, so no installation loses its
 * copy. Every scoped name begins {@link LOCAL_SYNC_SCOPED_PREFIX}, which is how the owner rule finds
 * them all (I-1244).
 */
export function localSyncScope(address: string | null): string | null {
  return isBuiltInDeploymentAddress(address) ? null : deploymentCredentialScope(address as string);
}

/** Every scoped IndexedDB name begins with this; the unscoped one is {@link LOCAL_SYNC_DB_NAME}. */
export const LOCAL_SYNC_SCOPED_PREFIX = `${DB_NAME}:`;

function recordId(collection: string, key: string): string {
  return `${collection}\u0000${key}`;
}

function hasIndexedDb(): boolean {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

/**
 * F-0534 PPD-2 (I-1244): the cache's database being deleted, for a different person signing in. Every
 * read and write of every driver in this window waits for it, so none reaches the last person's
 * records — an open queued behind it gets a fresh, empty database.
 */
let pendingDrop: Promise<void> | null = null;

/**
 * Deletes the cache's database. Open connections close themselves on the delete's `versionchange`
 * (`open`); one that does not (another window still on an old build) holds the delete, and an open
 * queued behind it still waits for it, so the deadline only stops this window waiting forever.
 */
export function dropLocalSyncDatabase(): Promise<void> {
  if (!hasIndexedDb()) return Promise.resolve();
  return dropDatabases([DB_NAME]);
}

/**
 * FR-9 + I-1244: the same owner rule for every scoped copy. A different person signing in must not
 * keep the last person's copies of a second deployment's work areas, so every database the browser
 * holds under {@link LOCAL_SYNC_SCOPED_PREFIX} is deleted, with the same wait for every read. A
 * browser that cannot list its databases (`indexedDB.databases` is missing) deletes none here; it is
 * every current browser and webview.
 */
export function dropScopedLocalSyncDatabases(): Promise<void> {
  if (!hasIndexedDb()) return Promise.resolve();
  return dropDatabases(scopedLocalSyncDatabaseNames());
}

async function scopedLocalSyncDatabaseNames(): Promise<string[]> {
  const list = window.indexedDB.databases?.bind(window.indexedDB);
  if (!list) return [];
  try {
    return (await list())
      .map((database) => database.name)
      .filter((name): name is string => !!name && name.startsWith(LOCAL_SYNC_SCOPED_PREFIX));
  } catch {
    return [];
  }
}

/** Deletes the named databases; reads of every driver wait until this and earlier drops are done. */
function dropDatabases(names: string[] | Promise<string[]>): Promise<void> {
  const earlier = pendingDrop;
  const dropping = (async () => {
    // The drop is pending before the names are known: a read that starts meanwhile waits for it.
    // Names already known are deleted at once, in the caller's own turn.
    await Promise.all(
      (Array.isArray(names) ? names : await names).map(
        (name) =>
          new Promise<void>((resolve) => {
            const deadline = setTimeout(resolve, INDEXED_DB_OPEN_DEADLINE_MS);
            const done = () => {
              clearTimeout(deadline);
              resolve();
            };
            const request = window.indexedDB.deleteDatabase(name);
            request.onsuccess = done;
            request.onerror = done;
          }),
      ),
    );
    await earlier;
  })();
  pendingDrop = dropping;
  void dropping.then(() => {
    if (pendingDrop === dropping) pendingDrop = null;
  });
  return dropping;
}

export class BrowserIndexedDbDriver implements LocalDataDriver {
  private dbPromise: Promise<IDBDatabase> | null = null;
  readonly databaseName: string;

  constructor(scope: string | null = null) {
    this.databaseName = scope ? `${LOCAL_SYNC_SCOPED_PREFIX}${scope}` : DB_NAME;
  }

  get<T>(collection: string, key: string): Promise<LocalDataRecord<T> | null> {
    return this.withStore('readonly', (store) => store.get(recordId(collection, key))).then(
      (value) => (value as StoredRecord<T> | undefined)?.record ?? null,
    );
  }

  put<T>(record: LocalDataRecord<T>): Promise<void> {
    const stored: StoredRecord<T> = {
      id: recordId(record.collection, record.key),
      collection: record.collection,
      key: record.key,
      record,
    };
    return this.withStore('readwrite', (store) => store.put(stored)).then(() => undefined);
  }

  delete(collection: string, key: string): Promise<void> {
    return this.withStore('readwrite', (store) => store.delete(recordId(collection, key))).then(
      () => undefined,
    );
  }

  list<T>(collection: string): Promise<Array<LocalDataRecord<T>>> {
    return this.withStore('readonly', (store) => {
      const index = store.index('collection');
      return index.getAll(collection);
    }).then((records) =>
      ((records as StoredRecord<T>[] | undefined) ?? []).map((stored) => stored.record),
    );
  }

  private async withStore<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    if (pendingDrop) await pendingDrop;
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const request = operation(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
      transaction.onerror = () =>
        reject(transaction.error ?? new Error('IndexedDB transaction failed.'));
    });
  }

  private open(): Promise<IDBDatabase> {
    if (!hasIndexedDb()) {
      return Promise.reject(new Error('IndexedDB is not available.'));
    }
    if (this.dbPromise) return this.dbPromise;
    const opening = new Promise<IDBDatabase>((resolve, reject) => {
      /*
        I-1127: an open that another connection holds up (`blocked`), or that never answers, used to
        leave this promise pending for good — and, kept, every later read waited on it too. It now
        fails after `INDEXED_DB_OPEN_DEADLINE_MS` or on `blocked`, and the next read opens afresh.
      */
      const deadline = setTimeout(
        () => reject(new Error('IndexedDB did not open in time.')),
        INDEXED_DB_OPEN_DEADLINE_MS,
      );
      const request = window.indexedDB.open(this.databaseName, DB_VERSION);
      request.onblocked = () => {
        clearTimeout(deadline);
        reject(new Error('IndexedDB is held open by another window of the app.'));
      };
      request.onupgradeneeded = () => {
        const db = request.result;
        const store = db.objectStoreNames.contains(STORE_NAME)
          ? request.transaction?.objectStore(STORE_NAME)
          : db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        if (store && !store.indexNames.contains('collection')) {
          store.createIndex('collection', 'collection', { unique: false });
        }
      };
      request.onsuccess = () => {
        clearTimeout(deadline);
        const db = request.result;
        // Another window asking for a newer version: let it, and open again next time.
        db.onversionchange = () => {
          db.close();
          if (this.dbPromise === opening) this.dbPromise = null;
        };
        resolve(db);
      };
      request.onerror = () => {
        clearTimeout(deadline);
        reject(request.error ?? new Error('Could not open IndexedDB.'));
      };
    });
    this.dbPromise = opening;
    opening.catch(() => {
      if (this.dbPromise === opening) this.dbPromise = null;
    });
    return opening;
  }
}

export class BrowserLocalStorageDriver implements LocalDataDriver {
  constructor(private readonly scope: string | null = null) {}

  get<T>(collection: string, key: string): Promise<LocalDataRecord<T> | null> {
    const raw = window.localStorage.getItem(this.key(collection, key));
    if (!raw) return Promise.resolve(null);
    try {
      return Promise.resolve(JSON.parse(raw) as LocalDataRecord<T>);
    } catch {
      return Promise.resolve(null);
    }
  }

  put<T>(record: LocalDataRecord<T>): Promise<void> {
    window.localStorage.setItem(this.key(record.collection, record.key), JSON.stringify(record));
    return Promise.resolve();
  }

  delete(collection: string, key: string): Promise<void> {
    window.localStorage.removeItem(this.key(collection, key));
    return Promise.resolve();
  }

  list<T>(collection: string): Promise<Array<LocalDataRecord<T>>> {
    const prefix = this.key(collection, '');
    const records: Array<LocalDataRecord<T>> = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(prefix)) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      try {
        records.push(JSON.parse(raw) as LocalDataRecord<T>);
      } catch {
        // Ignore corrupt local cache entries. The server remains authoritative.
      }
    }
    return Promise.resolve(records);
  }

  private key(collection: string, key: string): string {
    const prefix = this.scope ? `featit:local-sync@${this.scope}` : 'featit:local-sync';
    return `${prefix}:${collection}:${key}`;
  }
}

export class MemoryStorageDriver implements LocalDataDriver {
  private records = new Map<string, LocalDataRecord>();

  async get<T>(collection: string, key: string): Promise<LocalDataRecord<T> | null> {
    return (this.records.get(recordId(collection, key)) as LocalDataRecord<T> | undefined) ?? null;
  }

  async put<T>(record: LocalDataRecord<T>): Promise<void> {
    this.records.set(recordId(record.collection, record.key), record);
  }

  async delete(collection: string, key: string): Promise<void> {
    this.records.delete(recordId(collection, key));
  }

  async list<T>(collection: string): Promise<Array<LocalDataRecord<T>>> {
    return [...this.records.values()].filter(
      (record): record is LocalDataRecord<T> => record.collection === collection,
    );
  }
}

interface StoredRecord<T> {
  id: string;
  collection: string;
  key: string;
  record: LocalDataRecord<T>;
}

export function createBrowserLocalDataDriver(
  scope: string | null = localSyncScope(effectiveDeploymentAddress()),
): LocalDataDriver {
  // F-0709 ASIF-12: a tab holding an as-if view neither opens nor feeds the person's cache. What the
  // view of a guest sees must not land in the admin's device copy, nor be filled from it.
  if (asIfAccessToken() !== null) return new MemoryStorageDriver();
  if (hasIndexedDb()) return new BrowserIndexedDbDriver(scope);
  if (typeof window !== 'undefined' && window.localStorage) {
    return new BrowserLocalStorageDriver(scope);
  }
  return new MemoryStorageDriver();
}
